import localConstants from '@/constants'
import { isConfirmedLineNotReady } from '@/composables/orderLineStatusText'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'

const { ORDER_PRODUCT_SHIPMENT_STATUS: LIB_ORDER_PRODUCT_SHIPMENT_STATUS } = c
const { ORDER_STATUS, ORDER_PRODUCT_SHIPMENT_STATUS } = localConstants

// KP-4363: a partially confirmed order ships its confirmed lines once their
// product is ready to ship. Its other lines (readyToShip / inProcess) are
// pending confirmation and never offered.
const SHIPMENT_ORDER_STATUSES = [ORDER_STATUS.CONFIRMED, ORDER_STATUS.PARTIALLY_CONFIRMED]

const SHIPPABLE_LINE_STATUSES = {
  [ORDER_STATUS.CONFIRMED]: [
    LIB_ORDER_PRODUCT_SHIPMENT_STATUS.READY_TO_SHIP.LABEL,
    ORDER_PRODUCT_SHIPMENT_STATUS.PARTIALLY_SHIPPED.LABEL,
  ],
  [ORDER_STATUS.PARTIALLY_CONFIRMED]: [
    ORDER_PRODUCT_SHIPMENT_STATUS.CONFIRMED.LABEL,
    ORDER_PRODUCT_SHIPMENT_STATUS.PARTIALLY_SHIPPED.LABEL,
  ],
}

export const getShipSelectAllLabel = order => (order?.status === ORDER_STATUS.PARTIALLY_CONFIRMED
  ? 'Select All Products Available To Ship'
  : 'Select Ready To Ship Products')

export const enableShipmentFeature = orderData => {
  if (SHIPMENT_ORDER_STATUSES.includes(orderData.status)) {
    const shippableProducts = orderData.products.filter(
      product => SHIPPABLE_LINE_STATUSES[orderData.status].includes(product.status)
        && !isConfirmedLineNotReady(product),
    )
    if (shippableProducts.length) {
      orderData.isEnableShipmentFeature = true
      orderData.readyToShipProductsCount = shippableProducts.length
    }
    orderData.products.forEach(product => {
      // Securing the raw items for create shipment
      product.itemsForShipment = product.items
    })
  }
}

const ORDER_STATUS_CONFIG = {
  [ORDER_STATUS.NEW]: { variant: 'info', icon: 'DollarSignIcon' },
  [ORDER_STATUS.CANCELLED]: { variant: 'warning', icon: 'RotateCcwIcon' },
  [ORDER_STATUS.REJECTED]: { variant: 'danger', icon: 'XCircleIcon' },
  [ORDER_STATUS.ACCEPTED]: { variant: 'success', icon: 'StarIcon' },
  [ORDER_STATUS.PARTIALLY_CONFIRMED]: { variant: 'primary', icon: 'CheckSquareIcon' },
}

export const parseOrderVariant = status => ORDER_STATUS_CONFIG[status]?.variant ?? null

export const parseOrderIcon = status => ORDER_STATUS_CONFIG[status]?.icon ?? 'InfoIcon'

export const getProductItem = (items, modifiedItems = []) => {
  const currentItems = []
  items.forEach(item => {
    const productItem = JSON.parse(JSON.stringify(item))
    const index = modifiedItems.findIndex(
      modifiedItem => modifiedItem.skuId === item.skuId,
    )
    if (index >= 0) {
      productItem.value = modifiedItems[index].value
    }
    currentItems.push(productItem)
  })
  return currentItems
}

export const ORDER_SEARCH_FIELD = {
  RETAILER_NAME: 'retailerName',
  BRAND_NAME: 'brandName',
  COLLECTION_NAME: 'collectionName',
}

export const getSearchByEndpoint = (isBrand = false) => (isBrand ? ORDER_SEARCH_FIELD.RETAILER_NAME : ORDER_SEARCH_FIELD.BRAND_NAME)

const getStatusChangeForProduct = (productId, importedProductStatuses) => importedProductStatuses?.find(s => s.productId === productId)

const formatCartonProduct = (prod, statusChange) => {
  const productPayload = {
    productId: prod.productId,
    quantity: prod.quantity,
  }
  if (statusChange) productPayload.status = statusChange.status
  return productPayload
}

const collectVariantItems = variants => {
  const itemsList = []
  variants?.forEach(item => {
    Object.entries(item).forEach(([, v]) => {
      if (v.value >= 0) {
        itemsList.push({ skuId: v.skuId, value: v.value })
      }
    })
  })
  return itemsList
}

const formatLineProduct = (prod, statusChange) => {
  const productPayload = {
    productId: prod.productId,
    items: collectVariantItems(prod.variants),
  }
  if (statusChange) productPayload.status = statusChange.status
  return productPayload
}

export const formatProducts = (products, importedProductStatuses = []) => {
  const payload = []
  let cancelledProductsCount = 0
  products.forEach(prod => {
    const statusChange = getStatusChangeForProduct(
      prod.productId,
      importedProductStatuses,
    )
    const productPayload = prod.isCarton
      ? formatCartonProduct(prod, statusChange)
      : formatLineProduct(prod, statusChange)
    payload.push(productPayload)
    if (prod.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL) {
      cancelledProductsCount++
    }
  })
  return { payload, cancelledProductsCount }
}

export const flattenItems = data => data.flatMap(product => {
    if (product.items && product.items.length > 0) {
      return product.items.map(item => ({
        productId: product.productId,
        skuId: item.skuId,
        value: item.value,
      }))
    } else if (product.quantity !== undefined) {
      return [
        {
          productId: product.productId,
          skuId: null,
          value: product.quantity,
        },
      ]
    } else {
      return []
    }
  })
